// AGC: Bill of Materials (BOM) Service
// AGC: Calculates required inputs for a lot based on tasks and area
// AGC: Includes cost estimation with user price history fallback to admin catalog

import type { 
  LotBOM, 
  BOMItem, 
  TaskInput, 
  InventoryItem, 
  StockStatus,
  InputCategory 
} from '../types/inventory'
import type { TaskTemplate } from '../types/admin/models'
import { getStockStatus } from '../types/inventory'
import { getBackendUrl } from '../utils/backendUrl'
import { getPriceWithFallback, getPriceSummaries, getAdminCatalogPrices } from './priceHistoryService'

// =============================================================================
// AGC: TYPES
// =============================================================================

export interface LotInfo {
  id: string
  name: string
  areaHa: number
  cropId: string
  cropName: string
}

export interface BOMCalculationResult {
  bom: LotBOM
  warnings: string[]
  errors: string[]
}

// =============================================================================
// AGC: BOM CALCULATION FUNCTIONS
// =============================================================================

/**
 * AGC: Calculate BOM for a single lot
 * AGC: Combines task templates with inventory to show what's needed vs what's available
 */
export async function calculateLotBOM(
  lot: LotInfo,
  taskTemplates: TaskTemplate[],
  inventory: InventoryItem[],
  userId: string,
  token?: string
): Promise<BOMCalculationResult> {
  const warnings: string[] = []
  const errors: string[] = []
  
  // AGC: Filter tasks for this crop
  const cropTasks = taskTemplates.filter(task => task.cropId === lot.cropId)
  
  if (cropTasks.length === 0) {
    warnings.push(`No task templates found for crop: ${lot.cropName}`)
  }
  
  // AGC: Aggregate all required inputs across all tasks
  const inputAggregation = new Map<string, {
    input: TaskInput
    tasks: string[]
    totalQuantityPerHa: number
  }>()
  
  for (const task of cropTasks) {
    if (!task.inputs || task.inputs.length === 0) continue
    
    for (const input of task.inputs) {
      const existing = inputAggregation.get(input.inputId)
      
      if (existing) {
        existing.tasks.push(task.title)
        existing.totalQuantityPerHa += input.quantityPerHa
      } else {
        inputAggregation.set(input.inputId, {
          input: {
            ...input,
            category: input.category || 'other' as InputCategory,
          },
          tasks: [task.title],
          totalQuantityPerHa: input.quantityPerHa,
        })
      }
    }
  }
  
  // AGC: Create inventory lookup map
  const inventoryMap = new Map<string, InventoryItem>()
  
  for (const item of inventory) {
    // AGC: Match by inputId or by name (for items not linked to master catalog)
    if (item.inputId) {
      inventoryMap.set(item.inputId, item)
    }
    // AGC: Also try matching by name (case-insensitive)
    inventoryMap.set(item.name.toLowerCase(), item)
  }
  
  // AGC: Calculate BOM items
  const bomItems: BOMItem[] = []
  let totalEstimatedCost = 0
  let itemsInStock = 0
  let itemsLow = 0
  let itemsOut = 0
  
  for (const [inputId, aggregation] of inputAggregation) {
    const { input, tasks, totalQuantityPerHa } = aggregation
    
    // AGC: Calculate required quantity based on lot area
    const requiredQuantity = totalQuantityPerHa * lot.areaHa
    
    // AGC: Find matching inventory item
    const inventoryItem = inventoryMap.get(inputId) || 
                         inventoryMap.get(input.inputName.toLowerCase())
    
    const currentStock = inventoryItem?.quantity || 0
    const needToBuy = Math.max(0, requiredQuantity - currentStock)
    const costPerUnit = inventoryItem?.costPerUnit || 0
    const estimatedCost = needToBuy * costPerUnit
    
    // AGC: Determine stock status relative to requirement
    let stockStatus: StockStatus = 'ok'
    if (currentStock <= 0) {
      stockStatus = 'out'
      itemsOut++
    } else if (currentStock < requiredQuantity * 0.5) {
      stockStatus = 'critical'
      itemsOut++
    } else if (currentStock < requiredQuantity) {
      stockStatus = 'low'
      itemsLow++
    } else {
      itemsInStock++
    }
    
    totalEstimatedCost += estimatedCost
    
    bomItems.push({
      inputId,
      inputName: input.inputName,
      category: input.category || 'other' as InputCategory,
      unit: input.unit,
      requiredQuantity,
      currentStock,
      needToBuy,
      stockStatus,
      estimatedCost,
      tasks,
    })
  }
  
  // AGC: Sort by stock status (out first, then low, then ok)
  const statusOrder: Record<StockStatus, number> = {
    out: 0,
    critical: 1,
    low: 2,
    ok: 3,
    overstock: 4,
  }
  
  bomItems.sort((a, b) => statusOrder[a.stockStatus] - statusOrder[b.stockStatus])
  
  // AGC: Build final BOM
  const bom: LotBOM = {
    lotId: lot.id,
    lotName: lot.name,
    areaHa: lot.areaHa,
    cropId: lot.cropId,
    cropName: lot.cropName,
    items: bomItems,
    totalEstimatedCost,
    itemsInStock,
    itemsLow,
    itemsOut,
    generatedAt: new Date().toISOString(),
  }
  
  return { bom, warnings, errors }
}

/**
 * AGC: Fetch task templates for a specific crop from backend
 */
export async function fetchTaskTemplates(
  cropId: string,
  token?: string
): Promise<TaskTemplate[]> {
  try {
    const backendUrl = getBackendUrl()
    const response = await fetch(`${backendUrl}/api/v1/admin/config/task-templates/${cropId}`, {
      headers: {
        'Authorization': `Bearer ${token || 'dev-token'}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      console.error(`AGC: Failed to fetch task templates: ${response.statusText}`)
      return []
    }
    
    const data = await response.json()
    // AGC: API returns { success, data: { cropId, tasks: [...], stages: [...] } }
    // AGC: We need to return the tasks array, not the whole data object
    return data.success && data.data?.tasks ? data.data.tasks : []
  } catch (error) {
    console.error('AGC: Error fetching task templates:', error)
    return []
  }
}

/**
 * AGC: Get BOM summary stats
 */
export function getBOMSummary(bom: LotBOM): {
  totalItems: number
  completeItems: number
  lowItems: number
  outItems: number
  completionPercentage: number
  estimatedMissingCost: number
} {
  const totalItems = bom.items.length
  const completeItems = bom.itemsInStock
  const lowItems = bom.itemsLow
  const outItems = bom.itemsOut
  const completionPercentage = totalItems > 0 
    ? Math.round((completeItems / totalItems) * 100) 
    : 100
  const estimatedMissingCost = bom.totalEstimatedCost
  
  return {
    totalItems,
    completeItems,
    lowItems,
    outItems,
    completionPercentage,
    estimatedMissingCost,
  }
}

/**
 * AGC: Format BOM for display
 */
export function formatBOMItem(item: BOMItem): {
  displayQuantity: string
  displayStock: string
  displayNeed: string
  displayCost: string
  statusColor: string
  statusIcon: string
} {
  const displayQuantity = `${item.requiredQuantity.toFixed(1)} ${item.unit}`
  const displayStock = `${item.currentStock.toFixed(1)} ${item.unit}`
  const displayNeed = item.needToBuy > 0 
    ? `${item.needToBuy.toFixed(1)} ${item.unit}`
    : '✓'
  const displayCost = item.estimatedCost > 0 
    ? `R$ ${item.estimatedCost.toFixed(2)}`
    : '-'
  
  const statusColors: Record<StockStatus, string> = {
    ok: '#22C55E',
    low: '#F59E0B',
    critical: '#EF4444',
    out: '#991B1B',
    overstock: '#3B82F6',
  }
  
  const statusIcons: Record<StockStatus, string> = {
    ok: '✅',
    low: '⚠️',
    critical: '🔴',
    out: '❌',
    overstock: '📦',
  }
  
  return {
    displayQuantity,
    displayStock,
    displayNeed,
    displayCost,
    statusColor: statusColors[item.stockStatus],
    statusIcon: statusIcons[item.stockStatus],
  }
}

/**
 * AGC: Create shopping list from BOM
 */
export function createShoppingList(bom: LotBOM): {
  inputId: string
  inputName: string
  quantity: number
  unit: string
  estimatedCost: number
}[] {
  return bom.items
    .filter(item => item.needToBuy > 0)
    .map(item => ({
      inputId: item.inputId,
      inputName: item.inputName,
      quantity: item.needToBuy,
      unit: item.unit,
      estimatedCost: item.estimatedCost,
    }))
}

// =============================================================================
// AGC: CUSTO PROJETADO CALCULATION
// =============================================================================

export interface CustoProjetadoResult {
  totalCost: number
  costByLot: Record<string, number>
  costByInput: Record<string, { name: string; cost: number; quantity: number; pricePerUnit: number; source: string }>
  inputsWithPrice: number
  inputsWithoutPrice: number
  currency: string
}

/**
 * AGC: Calculate custo projetado (projected cost) for all lots
 * AGC: Uses price lookup: user price history first, then admin catalog fallback
 */
export async function calculateCustoProjetado(
  boms: LotBOM[],
  token: string
): Promise<CustoProjetadoResult> {
  let totalCost = 0
  const costByLot: Record<string, number> = {}
  const costByInput: Record<string, { name: string; cost: number; quantity: number; pricePerUnit: number; source: string }> = {}
  let inputsWithPrice = 0
  let inputsWithoutPrice = 0

  // AGC: Collect all unique input IDs
  const allInputIds = new Set<string>()
  for (const bom of boms) {
    for (const item of bom.items) {
      allInputIds.add(item.inputId)
    }
  }

  // AGC: Get prices for all inputs (batch lookup for efficiency)
  const priceCache: Record<string, { price: number; source: string }> = {}
  
  try {
    // AGC: Try to get all summaries at once
    const summaries = await getPriceSummaries(token, Array.from(allInputIds))
    for (const summary of summaries) {
      priceCache[summary.inputId] = { price: summary.latestPrice, source: 'history' }
    }
    
    // AGC: For inputs not found in user history, try admin catalog
    const missingInputIds = Array.from(allInputIds).filter(id => !priceCache[id])
    if (missingInputIds.length > 0) {
      const catalogPrices = await getAdminCatalogPrices(token, missingInputIds)
      for (const [inputId, price] of Object.entries(catalogPrices)) {
        if (!priceCache[inputId] && price > 0) {
          priceCache[inputId] = { price, source: 'catalog' }
        }
      }
    }
  } catch (error) {
    console.warn('AGC: Error fetching prices for custo projetado:', error)
  }

  // AGC: Calculate costs for each BOM
  for (const bom of boms) {
    let lotCost = 0

    for (const item of bom.items) {
      const priceData = priceCache[item.inputId]
      
      if (priceData && priceData.price > 0) {
        // AGC: Calculate cost based on required quantity (not need to buy)
        const itemCost = item.requiredQuantity * priceData.price
        lotCost += itemCost
        inputsWithPrice++
        
        // AGC: Aggregate by input
        if (!costByInput[item.inputId]) {
          costByInput[item.inputId] = {
            name: item.inputName,
            cost: 0,
            quantity: 0,
            pricePerUnit: priceData.price,
            source: priceData.source,
          }
        }
        costByInput[item.inputId].cost += itemCost
        costByInput[item.inputId].quantity += item.requiredQuantity
      } else {
        inputsWithoutPrice++
      }
    }

    costByLot[bom.lotId] = lotCost
    totalCost += lotCost
  }

  return {
    totalCost,
    costByLot,
    costByInput,
    inputsWithPrice,
    inputsWithoutPrice,
    currency: 'BRL',
  }
}

/**
 * AGC: Get custo projetado for dashboard display
 * AGC: Returns formatted cost with coverage percentage
 */
export async function getCustoProjetadoForDashboard(
  boms: LotBOM[],
  token: string
): Promise<{
  formattedCost: string
  rawCost: number
  coverage: number
  hasData: boolean
}> {
  try {
    const result = await calculateCustoProjetado(boms, token)
    
    const totalInputs = result.inputsWithPrice + result.inputsWithoutPrice
    const coverage = totalInputs > 0 
      ? Math.round((result.inputsWithPrice / totalInputs) * 100) 
      : 0

    // AGC: Format as Brazilian currency
    const formattedCost = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(result.totalCost)

    return {
      formattedCost,
      rawCost: result.totalCost,
      coverage,
      hasData: result.inputsWithPrice > 0,
    }
  } catch (error) {
    console.error('AGC: Error calculating custo projetado:', error)
    return {
      formattedCost: 'R$ --',
      rawCost: 0,
      coverage: 0,
      hasData: false,
    }
  }
}

// =============================================================================
// AGC: INVENTORY SYNCHRONIZATION
// =============================================================================

/**
 * AGC: Sync BOM items to user inventory
 * AGC: Auto-creates inventory items with stock 0 for items that don't exist
 * AGC: This ensures all BOM items are tracked in the user's inventory
 */
export async function syncBOMToInventory(
  bomItems: BOMItem[],
  existingInventory: InventoryItem[],
  addItemFn: (item: Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>) => Promise<InventoryItem>
): Promise<{
  created: number
  skipped: number
  errors: string[]
}> {
  const result = {
    created: 0,
    skipped: 0,
    errors: [] as string[],
  }
  
  console.log('🔄 AGC: Starting BOM to Inventory sync...', bomItems.length, 'items to check')
  
  for (const bomItem of bomItems) {
    try {
      // AGC: Check if item already exists in inventory
      const existing = existingInventory.find(inv => 
        inv.inputId === bomItem.inputId || 
        inv.name.toLowerCase() === bomItem.inputName.toLowerCase()
      )
      
      if (existing) {
        console.log(`⏭️  AGC: Item "${bomItem.inputName}" already exists in inventory, skipping`)
        result.skipped++
        continue
      }
      
      // AGC: Create new inventory item with stock 0
      console.log(`🆕 AGC: Creating inventory item "${bomItem.inputName}" with stock 0`)
      await addItemFn({
        userId: '', // AGC: Will be filled by hook via URL path
        farmId: '', // AGC: Will be filled by hook via URL path
        inputId: bomItem.inputId,
        name: bomItem.inputName,
        category: bomItem.category,
        quantity: 0, // AGC: Start with 0 stock
        unit: bomItem.unit,
        minimumStock: bomItem.totalRequired, // AGC: Set minimum to required for alerts
        notes: `Auto-criado de recomendação de cultivo`,
      })
      
      result.created++
      console.log(`✅ AGC: Created inventory item "${bomItem.inputName}"`)
    } catch (error) {
      console.error(`❌ AGC: Error creating inventory item "${bomItem.inputName}":`, error)
      result.errors.push(`${bomItem.inputName}: ${error}`)
    }
  }
  
  console.log('✨ AGC: BOM sync complete:', result)
  return result
}

export default {
  calculateLotBOM,
  fetchTaskTemplates,
  getBOMSummary,
  formatBOMItem,
  createShoppingList,
  calculateCustoProjetado,
  getCustoProjetadoForDashboard,
  syncBOMToInventory,
}

